import React, { useRef } from "react";
import { useLang } from "@/i18n";
import Reveal from "@/components/Reveal";
import { Star, ChevronLeft, ChevronRight, ExternalLink } from "lucide-react";
import { REVIEWS, REVIEW_STATS, TRIPADVISOR_URL, GOOGLE_URL } from "@/components/reviews";

const SOURCE = {
  tripadvisor: { label: "TripAdvisor", url: TRIPADVISOR_URL, color: "#34E0A1" },
  google: { label: "Google", url: GOOGLE_URL, color: "#4285F4" },
};

function Stars({ n = 5 }) {
  return (
    <div className="flex gap-1 text-[#C4A661]">
      {Array.from({ length: n }).map((_, k) => <Star key={k} size={14} fill="currentColor" strokeWidth={0} />)}
    </div>
  );
}

export default function TestimonialsSection() {
  const { t, lang } = useLang();
  const ref = useRef(null);
  const scroll = (dir) => ref.current?.scrollBy({ left: dir * (ref.current.clientWidth * 0.8), behavior: "smooth" });

  return (
    <section data-testid="testimonials-section" className="max-w-[1440px] mx-auto px-6 md:px-12 py-24 md:py-32">
      <div className="mb-12 md:mb-16 flex flex-wrap items-end justify-between gap-8">
        <div>
          <p className="eyebrow mb-4">{t("testimonials.kicker")}</p>
          <h2 className="font-serif-hc font-light text-4xl md:text-5xl lg:text-[56px] leading-[1.05] text-[#F5F2EA] max-w-3xl uppercase">
            {t("testimonials.title")}
          </h2>
        </div>
        <div className="flex flex-wrap gap-3">
          {Object.entries(SOURCE).map(([key, s]) => (
            <a key={key} href={s.url} target="_blank" rel="noreferrer" data-testid={`reviews-badge-${key}`}
              className="flex items-center gap-4 border border-[#1E2B25] bg-[#0C1310] px-5 py-3 hover:border-[#C4A661] transition-colors">
              <span className="w-2 h-2 rounded-full" style={{ background: s.color }} />
              <div>
                <p className="text-xs tracking-[0.2em] uppercase text-[#A3A095]">{s.label}</p>
                <p className="text-[#F5F2EA] font-serif-hc text-xl leading-none mt-1">
                  {REVIEW_STATS[key].rating.toFixed(1)} <span className="text-xs font-sans text-[#A3A095]">· {REVIEW_STATS[key].count} {t("testimonials.reviews")}</span>
                </p>
              </div>
              <ExternalLink size={14} className="text-[#A3A095]" />
            </a>
          ))}
        </div>
      </div>

      <div className="relative">
        <div ref={ref} data-testid="reviews-slider" className="flex gap-5 overflow-x-auto snap-x snap-mandatory scrollbar-hide -mx-6 px-6 md:mx-0 md:px-0 pb-2">
          {REVIEWS.map((r, i) => (
            <Reveal key={i} delay={(i % 3) * 80} className="snap-start shrink-0 w-[85%] sm:w-[60%] md:w-[calc(33.333%-14px)] bg-[#0C1310] border border-[#1E2B25] p-8 md:p-10 flex flex-col gap-6">
              <div className="flex items-center justify-between">
                <Stars />
                <span className="text-[10px] tracking-[0.2em] uppercase" style={{ color: SOURCE[r.source].color }}>{SOURCE[r.source].label}</span>
              </div>
              <p data-testid={`review-text-${i}`} className="font-serif-hc italic text-lg md:text-xl text-[#F5F2EA] leading-relaxed">"{r.text[lang] || r.text.es}"</p>
              <div className="mt-auto pt-6 border-t border-[#1E2B25] flex justify-between items-baseline">
                <p className="text-sm text-[#F5F2EA]">{r.name}</p>
                <p className="text-xs tracking-[0.18em] uppercase text-[#A3A095]">{r.date[lang] || r.date.es}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <div className="mt-8 flex gap-3 justify-end">
          <button data-testid="reviews-prev" onClick={() => scroll(-1)} aria-label="prev" className="w-11 h-11 border border-[#1E2B25] flex items-center justify-center text-[#F5F2EA] hover:border-[#C4A661] transition-colors"><ChevronLeft size={18} /></button>
          <button data-testid="reviews-next" onClick={() => scroll(1)} aria-label="next" className="w-11 h-11 border border-[#1E2B25] flex items-center justify-center text-[#F5F2EA] hover:border-[#C4A661] transition-colors"><ChevronRight size={18} /></button>
        </div>
      </div>
    </section>
  );
}
